import {colorScalePrimary, colorScaleBoolean} from "../theming/palette";

const plottingBasics = {
    height: 400,
    width: 900,
    marginLeft: 80,
    marginRight: 40,
    marginBottom: 80,
    marginTop: 100,
}

export const getBoxplotAnalyze = (plotData, t) => {
    let yData;
    if (plotData.runData.predictionDetails.experimentType === "Forecast") {
        yData = [
            plotData.runData.predictionDetails.CRPSListTrain,
            plotData.runData.predictionDetails.CRPSListVal,
        ];
    } else {
        yData = [
            plotData.runData.predictionDetails.AEListTrain,
            plotData.runData.predictionDetails.AEListVal,
        ];
    }

    // Box Plot Settings
    const dataBoxPlot = [
        t("analyzer.TrainingSet"),
        t("analyzer.ValidationSet"),
    ].map((name, index) => ({
        type: "box",
        y: yData[index],
        name,
        boxpoints: "all",
        marker: {
            size: 3,
        },
    }))

    const layoutBoxPlot = {
        ...plottingBasics,
        title: plotData.runData.predictionDetails.experimentType === "Forecast" ? t("analyzer.LabelCRPS") : t("analyzer.LabelAbsolutError"),
        showlegend: false,
    };
    return [dataBoxPlot, layoutBoxPlot]
}

export const getScatterplotAnalyze = (plotData, t) => {
    const truthAxis = Array.from(
        {length: plotData.runData.targetMax - plotData.runData.targetMin + 1},
        (_, i) => plotData.runData.targetMin + i
    );

    const trace1 = {
        x: plotData.runData.predictionDetails.targetListTrain,
        y: plotData.runData.predictionDetails.predictionsTrain,
        mode: "markers",
        type: "scatter",
        name: t("analyzer.LabelTrainingSetPredictions"),
    };

    // Define the trace for the predicted data
    const trace2 = {
        x: plotData.runData.predictionDetails.targetListVal,
        y: plotData.runData.predictionDetails.predictionsVal,
        mode: "markers",
        type: "scatter",
        name: t("analyzer.LabelValidationSetPredictions"),
    };

    const dataScatterPlot = [trace1, trace2];

    const layoutScatterPlot = {
        ...plottingBasics,
        title: t("analyzer.LabelScatterPlot"),
        xaxis: {
            title: t("analyzer.Actual"),
            range: [Math.min(...truthAxis), Math.max(...truthAxis)],
        },
        yaxis: {
            title: t("analyzer.Predicted"),
            range: [Math.min(...truthAxis), Math.max(...truthAxis)],
        },
        shapes: [
            {
                type: "line",
                x0: 0,
                y0: 0,
                x1: Math.max(...truthAxis),
                y1: Math.max(...truthAxis),
                line: {
                    color: "black",
                    width: 1,
                    dash: "dash",
                },
            },
        ],
    };

    return [dataScatterPlot, layoutScatterPlot]
}

export const getConfusionMatrix = (plotData, t) => {

    const dataConfusionMatrix = [
        {
            z: plotData.runData.predictionDetails[plotData.type],
            x: plotData.runData.predictionDetails.classNames,
            y: plotData.runData.predictionDetails.classNames,
            type: "heatmap",
            colorscale: colorScalePrimary,
            text: plotData.runData.predictionDetails[plotData.type].map((row) =>
                row.map((value) => String(value))
            ),
            hoverinfo: "text",
            reversescale: true,
            colorbar: {title: t("analyzer.ConfusionMatrixColorBarLabel")},
        },
    ];

    // update layout
    const layoutConfusionMatrix = {
        ...plottingBasics,
        marginLeft: 140,
        title: t(`analyzer.${plotData.type.charAt(0).toUpperCase() + plotData.type.slice(1)}Title`),
        annotations: [],
        xaxis: {title: t("analyzer.ConfusionMatrixXTitle")},
        yaxis: {title: t("analyzer.ConfusionMatrixYTitle")},
        textAuto: true,
    };

    // Add annotations to the layout
    for (let i = 0; i < dataConfusionMatrix[0].y.length; i++) {
        for (let j = 0; j < dataConfusionMatrix[0].x.length; j++) {
            const currentValue = dataConfusionMatrix[0].z[i][j];
            const textColor = currentValue > 40 ? "#ffffff" : "#000000"; // Adjust the text color based on the cell value
            const annotation = {
                xref: "x1",
                yref: "y1",
                x: dataConfusionMatrix[0].x[j],
                y: dataConfusionMatrix[0].y[i],
                text: currentValue.toFixed(2),
                font: {
                    color: textColor,
                },
                showarrow: false,
            };
            layoutConfusionMatrix.annotations.push(annotation);
        }
    }

    return [dataConfusionMatrix, layoutConfusionMatrix]
}

export const getScatterplotTime = (plotData, t, theme) => {
    const {currentTarget, runData} = plotData

    const dataTimeSeries =
        [
            {
                x: runData.timeSeriesPlotsIdx,
                y: runData.infoPlotsData[currentTarget],
                type: "scatter",
                mode: "lines+markers",
                marker: {color: theme.palette.secondary.dark},
            },
        ]

    const layoutTimeSeries =
        {
            ...plottingBasics,
            width: "100%",
            height: "auto",
            title: t("inspect.TargetOverTime"),
            xaxis: {
                title: "Time",
                tickformat: "%b\n%Y",
                tickmode: "auto",
                nticks: 10,
            }
            ,
            yaxis: {
                title: `${currentTarget}`,
            }
            ,
        }

    return [dataTimeSeries, layoutTimeSeries]
}

export const getScatterplotAFS = (plotData, t, theme) => {
    const dataLossAndFeatures =
        [
            {
                x: plotData.runData.epochs,
                y: plotData.runData.progressValRMSE,
                type: "scatter",
                mode: "lines+markers",
                marker: {color: theme.palette.primary.light, line: {width: 0.5}},
                name: t("AFSTrainingStatus.ScatterYAxis1Label"),
            },
            {
                x: plotData.runData.epochs,
                y: plotData.runData.progressNumFeatures,
                type: "scatter",
                mode: "lines+markers",
                marker: {color: theme.palette.primary.dark},
                yaxis: 'y2',
                name: t("AFSTrainingStatus.ScatterYAxis2Label"),
            },
        ]

    const layoutScatter =
        {
            ...plottingBasics,
            width: "100%",
            height: "auto",
            title: t("AFSTrainingStatus.ScatterTitle"),
            xaxis: {
                title: t("AFSTrainingStatus.ScatterXAxisLabel"),
                tickformat: "%b\n%Y",
                tickmode: "auto",
                nticks: 10,
            },
            yaxis: {
                title: t("AFSTrainingStatus.ScatterYAxis1Label"),
            },
            yaxis2: {
                title: t("AFSTrainingStatus.ScatterYAxis2Label"),
                overlaying: 'y',
                side: 'right',
            },
            // set the legend to the top right
            legend: {
                orientation: "h",
                yanchor: "bottom",
                y: 1.02,
                xanchor: "right",
                x: 1
            },
            // check if final data is available, if so add annotation
            annotations: (plotData.runData.finalDoubleLinFit ? [
                {
                    // intersection point
                    x: plotData.runData.finalIntersectX,
                    y: plotData.runData.finalIntersectY,
                    xref: 'x',
                    yref: 'y',
                    text: t("AFSTrainingStatus.ScatterIntersectLabel"),
                    showarrow: true,
                    arrowhead: 6,
                    arrowcolor: theme.palette.tertiary.dark,
                    borderpad: 1,
                    bgcolor: "rgba(255, 255, 255, 0.5)",
                    ax: 20,
                    ay: -30
                },
                {
                    // robust point
                    x: plotData.runData.finalRobustX,
                    y: plotData.runData.finalRobustY,
                    xref: 'x',
                    yref: 'y',
                    text: t("AFSTrainingStatus.ScatterRobustLabel"),
                    showarrow: true,
                    arrowhead: 6,
                    arrowcolor: theme.palette.success.main,
                    borderpad: 1,
                    bgcolor: "rgba(255, 255, 255, 0.5)",
                    ax: -20,
                    ay: -30
                },
            ] : [])
        }
    return [dataLossAndFeatures, layoutScatter]
}
export const getAFSHeatmap = (plotData, t) => {
    console.log("AFSHeatmap plotData", plotData, Object(plotData.runData.z).length )
    const dataHeatmap = [
        {
            z: plotData.runData.z, // list of the feature names
            x: plotData.runData.x,  // list of epochs.
            y: plotData.runData.y, // list of each feature whether it was selected or not for each epoch.
            type: "heatmap",
            colorscale: colorScaleBoolean,
            colorbar: {
                title: t("AFSTrainingStatus.HeatmapColorBarLabel"),
                len: 5 / (Object(plotData.runData.z).length),
                tickmode: "array",
                ticks: "outside",
                tick0: 0,
                dtick: 1,
                tickvals: [0, 1],
                ticktext: [t("AFSTrainingStatus.HeatmapColorMin"), t("AFSTrainingStatus.HeatmapColorMax"),]
            },
        },

        // check if final data is available, if so add annotation
        plotData.runData.finalDoubleLinFit ? {
            // intersection point
            x: Array(plotData.runData.y.length).fill(plotData.runData.finalRobustX),
            y: plotData.runData.y,
            mode: "lines",
            type: "scatter",
        } : {}

    ];

    const layoutHeatmap = {
        ...plottingBasics,
        width: "100%",
        height: 800,
        margin: {
            l: 250, // Adjust the left margin to accommodate longer labels
            r: -250
        },
        title: t("AFSTrainingStatus.HeatmapTitle"),
        xaxis: {
            title: t("AFSTrainingStatus.HeatmapXTitle"),
            tickmode: "array",
            tickVals: plotData.runData.x,
            showgrid: true,
            range: [Math.min(...plotData.runData.x), Math.max(...plotData.runData.x)],
        },
        yaxis: {
            title: t("AFSTrainingStatus.HeatmapYTitle"),
            tickFont: {size: 5},
            range: [0, Object(plotData.runData.y).length],
        },
        // check if final data is available, if so add annotation
        annotations: (plotData.runData.finalDoubleLinFit ? [
            {
                // intersection point
                x: plotData.runData.finalRobustX,
                y: plotData.runData.y[0],
                xref: 'x',
                yref: 'y',
                text: t("AFSTrainingStatus.ScatterRobustLabel"),
                borderpad: 1,
                bgcolor: "rgba(255, 255, 255, 0.5)",
                ax: 20,
                ay: -30
            },
        ] : [])

    };

    return [dataHeatmap, layoutHeatmap]
}

export const getHistogramplot = (plotData, t, theme) => {

    const {currentTarget, runData} = plotData
    const dataHistogram = [
        {
            x: runData.infoPlotsData[currentTarget],
            type: "histogram",
            mode: "lines+markers",
            marker: {color: theme.palette.secondary.dark},
        },
    ]
    const layoutHistogram = {
        ...plottingBasics,
        width: "100%",
        height: "auto",
        xaxis: {
            title: `${currentTarget}`,
        },
        yaxis: {
            title: "Count",
        },
        title: t("inspect.TargetDistribution"),
    }
    return [dataHistogram, layoutHistogram]
}

export const getFeatureImportancePlot = (plotData, t, theme) => {
    const {runData} = plotData
    const currentTarget = 0

    const dataHistogram = [
        {
            x: Object.values(runData.featureImportance[currentTarget]),
            y: Object.keys(runData.featureImportance[currentTarget]),
            type: "bar",
            mode: "lines+markers",
            orientation: "h",
            marker: {color: theme.palette.primary.main},
        },
    ]
    const layoutHistogram = {
        ...plottingBasics,
        width: "100%",
        height: `${Object.keys(runData.featureImportance[currentTarget]).length * 35}`,
        xaxis: {
            title: "Importance",
        },
        yaxis: {
            'categoryorder': 'total ascending',
            'ticksuffix': " " // workaround to add space between axis and labels
        },
        margin: {
            l: 250, // Adjust the left margin to accommodate longer labels
            r: -250
        },
        title: t("analyzer.LabelFeatureImportancePlot"),
    }
    return [dataHistogram, layoutHistogram]
}

export const getPGPEScatterPlotLayout = (key, maxEpochs, t) => {
    // Replace underscores with spaces and convert to uppercase
    const readableHyperparameter = key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    maxEpochs = parseInt(maxEpochs, 10)
    return {
        ...plottingBasics,
        hovermode: 'closest',
        height: 300,
        width: 600,
        title: readableHyperparameter,
        xaxis: {title: t("trainingStatus.OptimizerEpoch"), range: (maxEpochs > 1 ? [0, maxEpochs + 1] : 1)},
        yaxis: {"title": readableHyperparameter},
        colorscale: "Aggrnyl"
    }

}

export const getPGPEMetricPlotLayout = (maxEpochs, t) => ({
    ...plottingBasics,
    hovermode: 'closest',
    height: 400,
    width: 800,
    title: t("trainingStatus.Metric"),
    xaxis: {
        title: t("trainingStatus.OptimizerEpoch"),
        range: (parseInt(maxEpochs, 10) > 1 ? [0, parseInt(maxEpochs, 10) + 1] : 1)
    },
    yaxis: {"title": t("trainingStatus.Metric"),},
    colorscale: "Aggrnyl"
})
export const getPGPEParcoordPlotLayout = (t) => ({
    ...plottingBasics,
    hovermode: 'closest',
    height: 400,
    width: 800,
    title: t("trainingStatus.Parcoord"),
    colorscale: "Aggrnyl"
})